import { useEffect, useMemo, useState } from 'react'

import { usePermissions } from 'commons/customHooks/useAuth'
import useDebounce from 'commons/customHooks/useDebounce'
import { sumArray } from 'commons/utils/array'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { toAllFirstLetterUpper } from 'commons/utils/string'
import {
  Checkbox,
  DSInputType,
  type HeadItemType,
  Icon,
  Input,
  LoadingHavaianas,
  Modal,
  NotFound,
  Pagination,
  SlimButton,
  Table,
  Tooltip,
  Typography,
} from 'components'
import type { ActionPerm } from 'components/Structures/Header/constants'
import { availableCentersFactories } from 'constants/enums'
import {
  getAnalysisItems,
  interruptAnalysis,
  setAnalysisRecords,
} from 'store/SalesOrderContext/salesOrderSlice'
import { setSalesOrderCurrentTab } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

const AnalysisTab = () => {
  const dispatch = useDispatch()
  const {
    draftBody,
    analysisRecords,
    loadingMaterials: loading,
  } = useSelector((state) => state.salesOrder)
  const [searchValue, setSearchValue] = useState('')
  const [openFilterModal, setOpenFilterModal] = useState(false)
  const [confirmInterruptModal, setConfirmInterruptModal] = useState(false)
  const pageSize = 20
  const getTotalPages = () => Math.ceil(getTotalItems() / pageSize)
  const getTotalItems = () => analysisRecords.length
  const [currentPage, setCurrentPage] = useState<number>(1)

  const factoryFilterOptions = availableCentersFactories
  const [selectedFactoryFilters, setSelectedFactoryFilters] = useState<string[]>([])
  const [appliedFactoryFilters, setAppliedFactoryFilters] = useState<string[]>([])

  const materialStatusOptions = ['SEM COTA', 'SEM ESTOQUE']
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<string[]>([])
  const [appliedStatusFilters, setAppliedStatusFilters] = useState<string[]>([])

  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.ORDER, action)
  }

  useEffect(() => {
    dispatch(getAnalysisItems())
  }, [])

  const dispatchSearch = (value: string) => {
    dispatch(getAnalysisItems(value, appliedFactoryFilters, appliedStatusFilters))
  }
  const debounceSearch = useDebounce(dispatchSearch, 1000)
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleUpperCase()
    setSearchValue(value)
    debounceSearch(value)
  }

  const clearFilters = () => {
    setSelectedFactoryFilters([])
    setAppliedFactoryFilters([])
    setSelectedStatusFilters([])
    setAppliedStatusFilters([])
    setSearchValue('')
    dispatch(getAnalysisItems())
    setOpenFilterModal(false)
  }

  const sizesSet = useMemo(() => {
    const memoSet = new Set<string>()
    analysisRecords.forEach(({ tamanhos }) => {
      tamanhos.forEach(({ cd_tamh }) => {
        const t = { size: cd_tamh }
        memoSet.add(JSON.stringify(t))
      })
    })
    return memoSet
  }, [analysisRecords])

  const statusLabels = {
    'SEM COTA': 'Indisponível por cota',
    'SEM ESTOQUE': 'Indisponível por estoque',
  }
  const statusIcons = {
    'SEM COTA': <Icon color={theme.colors.alert.warning.dark} icon="report" size="15" />,
    'SEM ESTOQUE': <Icon color={theme.colors.alert.warning.dark} icon="box" size="15" />,
  }

  const tableLabels = (): HeadItemType[] => {
    const sizes = [...sizesSet].sort().map((size) => {
      const object: { size: string } = JSON.parse(size)

      return { columnName: object.size, label: object.size }
    })

    return [
      { columnName: '', label: '' },
      { columnName: 'Material', label: 'Material' },
      { columnName: 'DescricaoMaterial', label: 'Descrição' },
      { columnName: 'Cor', label: 'Cor' },
      { columnName: 'DescricaoCor', label: 'Descrição' },
      { columnName: 'Fabrica', label: 'Fábrica' },
      { columnName: 'Qtd', label: 'Qtd.' },
      { columnName: 'Valor', label: 'Valor (R$)' },
      { columnName: 'Desconto', label: 'Desconto (%)' },
      ...sizes,
    ]
  }

  const tableData = () => {
    const recordsData = analysisRecords
      .slice(pageSize * (currentPage - 1), pageSize * currentPage)
      .map((record, key) => {
        const sizes = [...sizesSet].sort().map((size) => {
          const object: { size: string } = JSON.parse(size)
          const foundSize = record.tamanhos.find((t) => t.cd_tamh === object.size)
          if (foundSize?.cd_tamh) {
            return record.cd_matr[0] === '7' // TODO: Replace magic number with constant
              ? numberToDecimalString(foundSize.qt_item)
              : numberToDecimalString(foundSize.qt_item_conv)
          }
          return <S.EmptyValue key={`${key}-${object.size}`}>0</S.EmptyValue>
        })

        return {
          checked: !!record.checked,
          content: [
            record.ds_moti_crit ? (
              <Tooltip
                arrow
                key={`icon-${record.cd_matr}-${record.cd_cor}`}
                placement="top"
                title={statusLabels[record.ds_moti_crit as keyof typeof statusLabels]}
              >
                <S.IconSpan>
                  {statusIcons[record.ds_moti_crit as keyof typeof statusLabels]}
                </S.IconSpan>
              </Tooltip>
            ) : (
              ''
            ),
            record.cd_matr,
            toAllFirstLetterUpper(record.ds_matr),
            record.cd_cor,
            toAllFirstLetterUpper(record.ds_cor),
            record.cd_cent_matr ?? '--',
            numberToDecimalString(record.qt_tota_item_conv),
            record.vl_tota_item ? formatToPTBRNumber(record.vl_tota_item) : '-',
            record.pc_dsct ? `${record.pc_dsct}%` : <S.EmptyValue>0%</S.EmptyValue>,
            ...sizes,
          ],
          id: `${record.cd_matr}-${record.cd_cor}-${record.nr_item_pedi_rasc}`,
        }
      })
    return recordsData
  }

  const setMaterialCheck = (checked: boolean, id?: string) => {
    if (id) {
      const nr_item_pedi_rasc = id.split('-')[2]

      const newRecords = analysisRecords.map((record) => {
        if (record.nr_item_pedi_rasc === Number(nr_item_pedi_rasc)) {
          return {
            ...record,
            checked,
          }
        }
        return record
      })
      dispatch(setAnalysisRecords(newRecords))
    } else {
      const newRecords = analysisRecords.map((record) => {
        return {
          ...record,
          checked,
        }
      })
      dispatch(setAnalysisRecords(newRecords))
    }
  }

  const getCheckedRecords = () => {
    return analysisRecords.filter((record) => record.checked)
  }

  const calcBoxes = () => {
    const valuesToSum = analysisRecords.map((item) => {
      return item.qt_tota_item
    })
    return valuesToSum.reduce(sumArray, 0)
  }
  const calcPairs = () => {
    const valuesToSum = analysisRecords.map((item) => {
      return item.qt_tota_item_conv
    })
    return valuesToSum.reduce(sumArray, 0)
  }
  const calcSelectedPairs = () => {
    const valuesToSum = getCheckedRecords().map((item) => {
      return item.qt_tota_item_conv
    })
    return numberToDecimalString(valuesToSum.reduce(sumArray, 0))
  }
  const calcTotal = () => {
    const valuesToSum = analysisRecords.map((item) => item.vl_tota_item || 0)
    return valuesToSum.reduce(sumArray, 0)
  }

  const toggleFactoryFilterItem = (option: string) => {
    setSelectedFactoryFilters((prev) => {
      if (prev.includes(option)) {
        return prev.filter((item) => item !== option)
      }
      return [...prev, option]
    })
  }

  const toggleStatusFilterItem = (option: string) => {
    setSelectedStatusFilters((prev) => {
      if (prev.includes(option)) {
        return prev.filter((item) => item !== option)
      }
      return [...prev, option]
    })
  }

  const hasSearch = () =>
    searchValue.length || appliedFactoryFilters.length || appliedStatusFilters.length

  const appliedFiltersAmount = () => {
    let amount = 0
    if (appliedFactoryFilters.length > 0) amount++
    if (appliedStatusFilters.length > 0) amount++
    return amount
  }

  const handleInterruptAnalysis = () => {
    const checkedRecords = getCheckedRecords()
    const items = new Set<number>()
    checkedRecords.forEach((record) => {
      items.add(record.nr_item_pedi_rasc)
    })
    if (checkedRecords.length === analysisRecords.length) {
      dispatch(setSalesOrderCurrentTab(1))
    }
    dispatch(
      interruptAnalysis(
        [...items].map((item) => {
          return {
            nr_item_pedi_rasc: item,
            nr_pedi_rasc: draftBody.nr_pedi_rasc,
          }
        }),
      ),
    )
    setConfirmInterruptModal(false)
  }

  return (
    <S.Wrapper>
      <S.HeadContainer>
        <S.TitleWrapper>
          <Typography design="hav-h5-text">Materiais pendentes por análise</Typography>
          <Typography design="hav-helper-text">
            Uma notificação será enviada assim que a análise for concluída e será possível
            prosseguir de acordo com o resultado.
          </Typography>
        </S.TitleWrapper>

        <S.SearchWrapper>
          <div>
            <Input
              addorment
              design={DSInputType.SEARCH_INPUT}
              label="Localizar material"
              onChange={handleSearch}
              placeholder="Ex.: 4000029"
              value={searchValue}
              width={165}
            />
          </div>
          <S.FilterButtonWrapper>
            <S.FilterButton disabled={loading} onClick={() => setOpenFilterModal(true)}>
              <Icon icon="filter" size="30" />
            </S.FilterButton>
            {appliedFiltersAmount() > 0 && (
              <S.FiltersNumberStyled>{appliedFiltersAmount()}</S.FiltersNumberStyled>
            )}
          </S.FilterButtonWrapper>
        </S.SearchWrapper>
      </S.HeadContainer>

      <S.Divider />
      {loading ? (
        <S.LoadingContainer>
          <LoadingHavaianas />
        </S.LoadingContainer>
      ) : (
        <>
          <S.ActionRow>
            <SlimButton
              content="Interromper análise"
              disabled={!getCheckedRecords().length || shouldDisable(ACTIONS_SCREENS.EDIT)}
              icon={<Icon icon="undo" size="15" />}
              onClick={() => setConfirmInterruptModal(true)}
              variant="secondary"
            />
            <Typography design="hav-default-03-text">
              {getCheckedRecords().length > 0
                ? `${calcSelectedPairs()} pares em análise selecionados`
                : 'Nenhum par em análise está selecionado'}
            </Typography>
          </S.ActionRow>

          {analysisRecords.length ? (
            <S.TableWrapper>
              <Table
                bodyData={tableData()}
                checkboxSize="small"
                hasCheckbox
                headData={tableLabels()}
                iconColumns={[0]}
                rowHeight={32}
                setChecked={setMaterialCheck}
              />
            </S.TableWrapper>
          ) : (
            <S.NotFoundWrapper>
              <NotFound
                description={
                  <S.NotFoundContent>
                    {hasSearch() ? (
                      <>
                        Verifique os critérios utilizados e tente novamente.
                        <SlimButton
                          content="Reiniciar busca e filtros"
                          icon={<Icon icon="chevron-left" size="15" />}
                          onClick={clearFilters}
                          type="button"
                          variant="white"
                        />
                      </>
                    ) : (
                      'Nenhum material pendente por análise neste pedido.'
                    )}
                  </S.NotFoundContent>
                }
                title={'Nenhum resultado encontrado'}
              />
            </S.NotFoundWrapper>
          )}

          <S.SummaryWrapper>
            <div>
              {getTotalItems() > pageSize && (
                <Pagination
                  currentPage={currentPage}
                  itemsPerPage={pageSize}
                  numberOfItems={getTotalItems()}
                  numberOfPages={getTotalPages()}
                  setCurrentPage={setCurrentPage}
                />
              )}
            </div>
            <S.SummaryRow>
              <S.SummaryItem>
                <Typography design="hav-default-02-text">Volume • pares</Typography>
                <span>{numberToDecimalString(calcPairs())}</span>
              </S.SummaryItem>
              <S.SummaryItem>
                <Typography design="hav-default-02-text">Volume • caixas</Typography>
                <span>{numberToDecimalString(calcBoxes())}</span>
              </S.SummaryItem>
              <S.SummaryItem>
                <Typography design="hav-default-02-text">Valor total</Typography>
                <span>{formatToPTBRNumber(calcTotal())}</span>
              </S.SummaryItem>
            </S.SummaryRow>
          </S.SummaryWrapper>
        </>
      )}

      <Modal
        closeAction={() => {
          setSelectedFactoryFilters(appliedFactoryFilters)
          setSelectedStatusFilters(appliedStatusFilters)
          setOpenFilterModal(false)
        }}
        modalWidth={450}
        open={openFilterModal}
        setOpen={setOpenFilterModal}
        showAction={false}
        title="Filtro de materiais"
        titleSize={22}
      >
        <S.FilterOption>
          <span>Fábrica</span>
          <S.CheckboxRow>
            {factoryFilterOptions.map((option) => (
              <Checkbox
                checked={selectedFactoryFilters.includes(option)}
                key={option}
                label={option}
                onChange={() => toggleFactoryFilterItem(option)}
                size="small"
              />
            ))}
          </S.CheckboxRow>
        </S.FilterOption>

        <S.FilterOption>
          <span>Status</span>
          <S.CheckboxList>
            {materialStatusOptions.map((option) => (
              <Checkbox
                checked={selectedStatusFilters.includes(option)}
                key={option}
                label={
                  <S.IconSpan>
                    {statusIcons[option as keyof typeof statusLabels]}
                    {statusLabels[option as keyof typeof statusLabels]}
                  </S.IconSpan>
                }
                onChange={() => toggleStatusFilterItem(option)}
                size="small"
              />
            ))}
          </S.CheckboxList>
        </S.FilterOption>

        <S.ModalActionsRow>
          <SlimButton content="Limpar filtros" onClick={clearFilters} variant="secondary" />
          <SlimButton
            content="Filtrar"
            onClick={() => {
              setAppliedFactoryFilters(selectedFactoryFilters)
              setAppliedStatusFilters(selectedStatusFilters)
              dispatch(getAnalysisItems(searchValue, selectedFactoryFilters, selectedStatusFilters))
              setOpenFilterModal(false)
            }}
            variant="primary"
          />
        </S.ModalActionsRow>
      </Modal>
      <Modal
        closeAction={() => setConfirmInterruptModal(false)}
        description={`Seu material retornará para a aba “Em aberto” como um rascunho, permitindo que
        você edite ou adicione algumas configurações e quantidades.`}
        modalWidth={450}
        open={confirmInterruptModal}
        setOpen={setConfirmInterruptModal}
        showAction={false}
        title="Interromper análise"
        titleSize={22}
      >
        <S.ModalActionsRow>
          <SlimButton
            content="Voltar"
            onClick={() => setConfirmInterruptModal(false)}
            variant="secondary"
          />
          <SlimButton content="Confirmar" onClick={handleInterruptAnalysis} variant="primary" />
        </S.ModalActionsRow>
      </Modal>
    </S.Wrapper>
  )
}

export default AnalysisTab
